import React from 'react';
import BaseDrawer from './BaseDrawer';
import { ImpactAction } from '../../contexts/AdminDataContext';
import { Leaf, Users, TrendingUp, Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
interface ImpactActionDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  action: ImpactAction | null;
}
const ImpactActionDetailDrawer: React.FC<ImpactActionDetailDrawerProps> = ({
  isOpen,
  onClose,
  action
}) => {
  if (!action) return null;
  // Generate mini chart data for completions trend
  const chartData = Array.from({
    length: 7
  }, (_, i) => ({
    day: ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'][i],
    value: Math.round(action.completions * 0.12 + Math.random() * action.completions * 0.05)
  }));
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Alimentazione':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'Mobilità':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Casa':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Shopping':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Ambiente':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };
  return <BaseDrawer isOpen={isOpen} onClose={onClose} title={action.name} description={`Categoria: ${action.category}`} footer={<button onClick={onClose} className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" data-id="element-648">
          Chiudi
        </button>} data-id="element-647">
      {/* Category Badge */}
      <div className="flex items-center gap-2 mb-4" data-id="element-649">
        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(action.category)}`} data-id="element-650">
          {action.category}
        </span>
      </div>

      {/* Description */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6" data-id="element-651">
        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-652">
          {action.description}
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-653">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-654">
          <div className="flex items-center gap-2 mb-2" data-id="element-655">
            <Users className="h-4 w-4 text-blue-600" data-id="element-656" />
            <p className="text-xs font-medium text-blue-700" data-id="element-657">Completamenti</p>
          </div>
          <p className="text-2xl font-bold text-blue-900" data-id="element-658">
            {action.completions.toLocaleString()}
          </p>
        </div>

        <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-659">
          <div className="flex items-center gap-2 mb-2" data-id="element-660">
            <Leaf className="h-4 w-4 text-green-600" data-id="element-661" />
            <p className="text-xs font-medium text-green-700" data-id="element-662">
              CO₂ Risparmiata
            </p>
          </div>
          <p className="text-2xl font-bold text-green-900" data-id="element-663">
            {action.co2.toLocaleString()} kg
          </p>
        </div>

        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200" data-id="element-664">
          <div className="flex items-center gap-2 mb-2" data-id="element-665">
            <TrendingUp className="h-4 w-4 text-purple-600" data-id="element-666" />
            <p className="text-xs font-medium text-purple-700" data-id="element-667">
              CO₂ per Azione
            </p>
          </div>
          <p className="text-2xl font-bold text-purple-900" data-id="element-668">
            {(action.co2 / action.completions).toFixed(2)} kg
          </p>
        </div>

        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200" data-id="element-669">
          <div className="flex items-center gap-2 mb-2" data-id="element-670">
            <Calendar className="h-4 w-4 text-amber-600" data-id="element-671" />
            <p className="text-xs font-medium text-amber-700" data-id="element-672">Ultimo Agg.</p>
          </div>
          <p className="text-sm font-bold text-amber-900" data-id="element-673">
            {action.lastUpdated}
          </p>
        </div>
      </div>

      {/* Trend Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-674">
        <h4 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-675">
          Trend Completamenti (Ultimi 7 Giorni)
        </h4>
        <div className="h-48" data-id="element-676">
          <ResponsiveContainer width="100%" height="100%" data-id="element-677">
            <LineChart data={chartData} data-id="element-678">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-679" />
              <XAxis dataKey="day" stroke="#6B7280" fontSize={12} data-id="element-680" />
              <YAxis stroke="#6B7280" fontSize={12} data-id="element-681" />
              <Tooltip contentStyle={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '0.5rem'
            }} data-id="element-682" />
              <Line type="monotone" dataKey="value" stroke="#108d3e" strokeWidth={2} dot={{
              fill: '#108d3e',
              r: 4
            }} data-id="element-683" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Impact Breakdown */}
      <div className="bg-green-50 rounded-lg p-4 border border-green-200 mt-6" data-id="element-684">
        <h4 className="text-sm font-semibold text-green-900 mb-3" data-id="element-685">
          Impatto Totale Stimato
        </h4>
        <div className="space-y-2 text-sm" data-id="element-686">
          <div className="flex justify-between" data-id="element-687">
            <span className="text-green-700" data-id="element-688">Emissioni CO₂ evitate:</span>
            <span className="font-bold text-green-900" data-id="element-689">
              {action.co2.toLocaleString()} kg
            </span>
          </div>
          <div className="flex justify-between" data-id="element-690">
            <span className="text-green-700" data-id="element-691">Equivalente a:</span>
            <span className="font-bold text-green-900" data-id="element-692">
              {Math.round(action.co2 / 21)} alberi piantati
            </span>
          </div>
          <div className="flex justify-between" data-id="element-693">
            <span className="text-green-700" data-id="element-694">Partecipazione:</span>
            <span className="font-bold text-green-900" data-id="element-695">
              {(action.completions / 4235 * 100).toFixed(1)}% degli utenti
            </span>
          </div>
        </div>
      </div>
    </BaseDrawer>;
};
export default ImpactActionDetailDrawer;